import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';

let nextId = 0;

export interface NotificationOptions {
  severity: string;
  summary: string;
  detail?: string;
  autoCloseMs?: number;
  /** Stable key for condition-based notifications. Replaces existing notification with same key. */
  conditionKey?: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private notificationsSubject = new BehaviorSubject<ToastMessageOptions[]>([]);
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  /** Observable for the banner component */
  readonly notifications$ = this.notificationsSubject.asObservable();

  /**
   * Add a notification to the banner.
   * If a conditionKey is provided and one already exists, the existing notification is replaced.
   * @returns The unique id of the notification
   */
  add(severity: string, summary: string, detail?: string, autoCloseMs?: number, conditionKey?: string): string {
    const id = `notif-${++nextId}`;

    // If conditionKey provided, remove any existing notification with that key
    if (conditionKey) {
      this.resolveByKey(conditionKey);
    }

    const message: ToastMessageOptions = {
      severity,
      summary,
      detail,
      id,
      closable: true,
      data: { conditionKey },
    };

    const current = this.notificationsSubject.value;
    this.notificationsSubject.next([...current, message]);

    if (autoCloseMs != null && autoCloseMs > 0) {
      const timer = setTimeout(() => {
        this.remove(id);
        this.timers.delete(id);
      }, autoCloseMs);
      this.timers.set(id, timer);
    }

    return id;
  }

  /** Remove a notification by id */
  remove(id: string): void {
    const current = this.notificationsSubject.value;
    this.notificationsSubject.next(current.filter(m => m.id !== id));

    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  /** Remove all notifications matching a conditionKey */
  resolveByKey(conditionKey: string): void {
    const current = this.notificationsSubject.value;
    const toRemove = current.filter(m => m.data?.conditionKey === conditionKey);
    toRemove.forEach(m => {
      const timer = this.timers.get(m.id);
      if (timer) {
        clearTimeout(timer);
        this.timers.delete(m.id);
      }
    });
    this.notificationsSubject.next(current.filter(m => m.data?.conditionKey !== conditionKey));
  }

  /** Clear all notifications */
  clear(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.notificationsSubject.next([]);
  }

  ngOnDestroy(): void {
    this.clear();
  }
}
